import {
    useState,
    type Dispatch,
    type SetStateAction,
    type ReactNode,
    type HTMLAttributes
} from 'react'
import { useNodes, useReactFlow, ViewportPortal, XYPosition } from '@xyflow/react'

const NodeInfo = ({ id, parentId, type, selected, position, absPosition, width, height }: NodeInfoProps) => {
    if (!width || !height) {
        return null
    }

    return (
        <div
            className='react-flow__devtools-nodeinfo'
            style={{
                position: 'absolute',
                transform: `translate(${absPosition.x}px, ${absPosition.y + height}px)`,
                width: width * 2
            }}>
            <div>id: {id}</div>
            <div>parent id: {parentId}</div>
            <div>type: {type}</div>
            <div>selected: {selected ? 'true' : 'false'}</div>
            <div>
                position: {position.x.toFixed(1)}, {position.y.toFixed(1)}
            </div>
            <div>
                absolute position: {absPosition.x.toFixed(1)}, {absPosition.y.toFixed(1)}
            </div>
            <div>
                dimensions: {width} × {height}
            </div>
        </div>
    )
}

const NodeInspector = () => {
    const { getInternalNode } = useReactFlow()
    const nodes = useNodes()

    return (
        <ViewportPortal>
            <div className='react-flow__devtools-nodeinspector'>
                {nodes.map(node => {
                    const internalNode = getInternalNode(node.id)
                    if (!internalNode) {
                        return null
                    }

                    const absPosition = internalNode?.internals.positionAbsolute

                    return (
                        <NodeInfo
                            key={node.id}
                            id={node.id}
                            parentId={node?.parentId ?? null}
                            selected={!!node.selected}
                            type={node.type || 'default'}
                            position={node.position}
                            absPosition={absPosition}
                            width={node.measured?.width ?? 0}
                            height={node.measured?.height ?? 0}
                        />
                    )
                })}
            </div>
        </ViewportPortal>
    )
}

const NodeDebugUiButton = ({ active, setActive, children, ...rest }: DevToolButtonProps) => {
    return (
        <button
            onClick={() => setActive(a => !a)}
            className={active ? 'active' : ''}
            {...rest}>
            {children}
        </button>
    )
}

const NodeDebugUiInfo = () => {
    const [nodeInspectorActive, setNodeInspectorActive] = useState(false)

    return (
        <div className='react-flow__devtools'>
            <NodeDebugUiButton
                style={{ width: 70 }}
                setActive={setNodeInspectorActive}
                active={nodeInspectorActive}
                title='Toggle Node Inspector'>
                Debug UI
            </NodeDebugUiButton>
            {nodeInspectorActive && <NodeInspector />}
        </div>
    )
}

export default NodeDebugUiInfo

type NodeInfoProps = {
    id: string
    parentId: string
    type: string
    selected: boolean
    position: XYPosition
    absPosition: XYPosition
    width?: number
    height?: number
}

type DevToolButtonProps = {
    active: boolean
    setActive: Dispatch<SetStateAction<boolean>>
    children: ReactNode
} & HTMLAttributes<HTMLButtonElement>
